import { useCallback, useLayoutEffect, useRef } from "react";

function scrollParent(el) {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === "auto" || overflowY === "scroll") && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * Keeps a clicked element at the same spot on screen across the re-render
 * its click causes — e.g. opening one row collapses another above it, which
 * would otherwise slide the row you tapped up and away from the pointer.
 *
 * Call `keep(event.currentTarget)` right before the state change. Must live
 * in the component whose state changes, since the correction runs in that
 * component's layout effect (after the DOM updates, before paint).
 */
export default function useKeepInPlace() {
  const pending = useRef(null);

  useLayoutEffect(() => {
    const p = pending.current;
    if (!p) return;
    pending.current = null;
    if (!p.el.isConnected) return;
    const delta = p.el.getBoundingClientRect().top - p.top;
    if (Math.abs(delta) < 1) return;
    const container = scrollParent(p.el);
    if (container) container.scrollTop += delta;
    else window.scrollBy(0, delta);
  });

  return useCallback((el) => {
    if (el) pending.current = { el, top: el.getBoundingClientRect().top };
  }, []);
}
